import { Stack, Flex } from "@chakra-ui/react";
import { Dispatch, FC, ReactNode, SetStateAction, useEffect } from "react";
import FormFooter from "../../molecule/FormFooter/FormFooter";
import { unknown } from "zod";
import AlertDialogComponent from "../../molecule/AlertDialog/AlertDialogComponent";
import {
  CANCEL_DIALOG_MESSAGE,
  DELETE_DIALOG_MESSAGE,
} from "../../../utils/constants";
import { useScroll } from "../../../provider/ScrollProvider";
import SubmitItemHeader from "./SubmitItemHeader";
import SubmitItemContent from "./SubmitItemContent";
import { useLocation, useNavigate } from "react-router-dom";
import { StepperConfig } from "../../../types/Components/Stepper/StepperConfig";
import { AlertDialogConfig } from "../../../types/Components/Alert/AlertDialogConfig";
import { AlertConfig } from "../../../types/Components/Alert/AlertConfig";
import { useDispatch } from "react-redux";
import { setWithConfirm } from "../../../redux/slices/navigationSlice";
import { goBack } from "../../../helpers/goBack";

interface SubmitItemProps {
  title: string | JSX.Element;
  formTitle?: string;
  children: ReactNode;
  hasSubmitFooter?: boolean;
  hasStepper?: boolean;
  handleSubmitItem?: () => void;
  handleDeleteItem?: () => void;
  deleteButtonName?: string;
  isEditing?: boolean;
  setIsEditing?: Dispatch<SetStateAction<boolean>>;
  navigatePath?: string;
  lastUpdateDate?: string;
  maxWidth?: string;
  hasAlert?: boolean;
  stepperConfig?: StepperConfig;
  alertDialogConfig?: AlertDialogConfig;
  alertConfig?: AlertConfig;
  isLoaded: boolean;
  handleStateChange?: (isActive: boolean) => void;
  initialIsActive?: boolean;
  isDirty?: boolean;
}

const SubmitItem: FC<SubmitItemProps> = ({
  title,
  formTitle,
  children,
  hasSubmitFooter,
  hasStepper,
  handleSubmitItem,
  handleDeleteItem,
  deleteButtonName,
  isEditing,
  setIsEditing,
  navigatePath = "",
  lastUpdateDate,
  maxWidth,
  hasAlert = false,
  stepperConfig = undefined,
  alertDialogConfig = undefined,
  alertConfig = undefined,
  isLoaded = true,
  handleStateChange,
  initialIsActive = false,
  isDirty = false,
}) => {
  const {
    nextButtonName,
    finishButtonName,
    steps = [],
    onNext = unknown,
    onBack = unknown,
    isNextActive = false,
    isFinalStep = false,
    currentStep = 0,
    isButtonDisabled = false,
    isReturnButtonDisabled = false,
  } = stepperConfig || {};

  const {
    isAlertOpen,
    setIsAlertOpen,
    alertButtonText = "Sair",
    alertTitle = "Sair sem criar",
  } = alertDialogConfig || {};

  const { isScrolled } = useScroll();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const handleCancel = () => {
    setIsAlertOpen && setIsAlertOpen(true);
  };

  const handleDialogButton = () => {
    if (handleDeleteItem) {
      handleDeleteItem();
      navigate(navigatePath);
    } else {
      goBack(navigate, pathname);
    }
  };

  const withConfirmCallback = (cancelCallback: () => void) => {
    setIsAlertOpen && setIsAlertOpen(true);
    cancelCallback();
  };

  useEffect(() => {
    if (!handleDeleteItem && isDirty) {
      dispatch(setWithConfirm(withConfirmCallback));

      return () => {
        dispatch(setWithConfirm(null));
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isDirty]);

  return (
    <Flex direction="column" justifyContent="space-between">
      <Stack spacing={8} flex="1" pb={hasSubmitFooter ? "132px" : "24px"}>
        <SubmitItemHeader
          title={title}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          lastUpdateDate={lastUpdateDate}
          handleStateChange={handleStateChange}
          initialIsActive={initialIsActive}
        />
        <SubmitItemContent
          hasStepper={hasStepper}
          steps={steps}
          currentStep={currentStep}
          hasAlert={hasAlert}
          formTitle={formTitle}
          children={children}
          handleDeleteItem={handleDeleteItem}
          handleSubmitItem={handleSubmitItem}
          deleteButtonName={deleteButtonName}
          isEditing={isEditing}
          setIsAlertOpen={setIsAlertOpen}
          isAlertOpen={isAlertOpen}
          maxWidth={maxWidth}
          alertConfig={alertConfig}
          isLoaded={isLoaded}
        />
      </Stack>

      {hasSubmitFooter && (
        <FormFooter
          isReturnActive={currentStep !== 0}
          isNextActive={isNextActive}
          onBack={onBack}
          onNext={onNext}
          handleCancel={handleCancel}
          isFinalStep={isFinalStep}
          isScrolled={isScrolled}
          nextButtonName={nextButtonName}
          finishButtonName={finishButtonName}
          nextButtonDisabled={isButtonDisabled ? isButtonDisabled : false}
          returnButtonDisabled={
            isReturnButtonDisabled ? isReturnButtonDisabled : false
          }
          isLoaded={isLoaded}
          pathname={pathname}
          navigate={navigate}
        />
      )}

      {isAlertOpen && setIsAlertOpen && (
        <AlertDialogComponent
          isOpen={isAlertOpen}
          setIsOpen={setIsAlertOpen}
          title={handleDeleteItem ? "Eliminar item" : alertTitle}
          text={
            handleDeleteItem ? DELETE_DIALOG_MESSAGE : CANCEL_DIALOG_MESSAGE
          }
          handleButton={handleDialogButton}
          buttonText={handleDeleteItem ? "Eliminar" : alertButtonText}
        />
      )}
    </Flex>
  );
};

export default SubmitItem;
